import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Heart, Monitor, AlertTriangle, Users, TrendingDown } from 'lucide-react';
import { API_URL } from '@/services/apiClient';

interface WellnessData {
  totalStudents: number;
  tracked: number;
  untracked: number;
  avgScreenTime: number;
  avgScore: number;
  belowThreshold: number;
  atRiskStudents: Array<{ studentId: string; name: string; score: number; screenTime: number }>;
  distribution: Array<{ label: string; min: number; max: number; count: number }>;
}

function ScoreRing({ score, size = 80 }: { score: number; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth="6" className="text-slate-200 dark:text-slate-700" />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="6"
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  );
}

export function WellnessDashboard({ dm }: { dm: boolean }) {
  const [data, setData] = useState<WellnessData | null>(null);
  const [loading, setLoading] = useState(true);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-white' : 'text-slate-800';
  const descCls = dm ? 'text-white' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-white' : 'text-slate-700';
  const mutedCls = dm ? 'text-white' : 'text-slate-400';
  const dividerCls = dm ? 'divide-slate-700' : 'divide-slate-100';

  const fetch_ = useCallback(async () => {
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/wellness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch {/* */} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const maxDist = Math.max(...(data?.distribution.map(d => d.count) ?? [1]), 1);

  const scoreColor = (s: number) => s >= 70 ? 'text-emerald-500' : s >= 40 ? 'text-amber-500' : 'text-red-500';

  return (
    <div className="space-y-5">
      <h2 className={`text-lg font-semibold ${titleCls}`}>Digital Wellness Overview</h2>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                    <Users className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Tracked Students</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{data?.tracked ?? 0}</p>
                    <p className={`text-xs ${mutedCls}`}>of {data?.totalStudents ?? 0} total</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Heart className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Avg Wellness Score</p>
                    <p className={`text-2xl font-bold ${scoreColor(data?.avgScore ?? 0)}`}>{data?.avgScore ?? 0}</p>
                    <p className={`text-xs ${mutedCls}`}>School average</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                    <Monitor className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Avg Screen Time</p>
                    <p className={`text-2xl font-bold ${titleCls}`}>{data?.avgScreenTime ?? 0}m</p>
                    <p className={`text-xs ${mutedCls}`}>Per student</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={cardCls}>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${dm ? 'bg-red-900/50' : 'bg-red-100'}`}>
                    <TrendingDown className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className={`text-xs ${descCls}`}>Below Threshold</p>
                    <p className={`text-2xl font-bold text-red-500`}>{data?.belowThreshold ?? 0}</p>
                    <p className={`text-xs ${mutedCls}`}>Score &lt; 40</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Score Distribution */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base ${titleCls}`}>Score Distribution</CardTitle>
                <CardDescription className={descCls}>Students by wellness score range</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.distribution && data.distribution.some(d => d.count > 0) ? (
                  <div className="space-y-3">
                    {data.distribution.map((d, i) => {
                      const color = d.max <= 20 ? 'bg-red-500' : d.max <= 40 ? 'bg-orange-500' : d.max <= 60 ? 'bg-amber-400' : d.max <= 80 ? 'bg-blue-400' : 'bg-emerald-500';
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className={`text-xs w-14 shrink-0 ${mutedCls}`}>{d.label}</span>
                          <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                            <div className={`h-full rounded-full transition-all duration-500 ${color}`}
                              style={{ width: `${Math.round((d.count / maxDist) * 100)}%` }} />
                          </div>
                          <span className={`text-xs font-semibold w-6 text-right shrink-0 ${dm ? 'text-white' : 'text-slate-700'}`}>{d.count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                    <Heart className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">No wellness data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* At-Risk Students */}
            <Card className={cardCls}>
              <CardHeader className="pb-3">
                <CardTitle className={`text-base flex items-center gap-2 ${titleCls}`}>
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Students Needing Attention
                </CardTitle>
                <CardDescription className={descCls}>Lowest wellness scores</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.atRiskStudents && data.atRiskStudents.length > 0 ? (
                  <div className={`divide-y ${dividerCls}`}>
                    {data.atRiskStudents.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 py-2.5">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          s.score < 20 ? (dm ? 'bg-red-900/60 text-red-400' : 'bg-red-100 text-red-600') :
                          s.score < 40 ? (dm ? 'bg-orange-900/60 text-orange-400' : 'bg-orange-100 text-orange-600') :
                          (dm ? 'bg-amber-900/60 text-amber-400' : 'bg-amber-100 text-amber-600')
                        }`}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${bodyTextCls}`}>{s.name}</p>
                          <p className={`text-xs ${mutedCls}`}>{s.screenTime}m screen time</p>
                        </div>
                        <div className="relative shrink-0">
                          <ScoreRing score={s.score} size={44} />
                          <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold rotate-90 ${scoreColor(s.score)}`}>{s.score}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`flex flex-col items-center justify-center h-32 ${mutedCls}`}>
                    <Heart className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">All students within healthy range</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
